import React from 'react';
import { SeeMoreTextCollapsible } from 'ui/SeeMoreTextCollapsible';
import { ColumnBulletPoints } from 'ui/ColumnBulletPoints/ColumnBulletPoints';
import { useCurrentWidth } from 'effects';

interface IHotelDetailsSectionProps {
  name: string;
  description: string | null;
  mealPlanCategories: string[] | null;
  amenities: string[] | null;
  highlights: string[] | null;
  inLoveWith: string | null;
  additionalInfo: string | null;
}

/**
 * HotelDetailsSection Component
 * Renders the main hotel details section including description, meal plans,
 * amenities, highlights, "in love with", and "things to be aware" content.
 */
export const HotelDetailsSection: React.FC<IHotelDetailsSectionProps> = ({
  name,
  description,
  mealPlanCategories,
  amenities,
  highlights,
  inLoveWith,
  additionalInfo,
}) => {
  const { isMdOrLarger } = useCurrentWidth();

  const hasMealPlans = mealPlanCategories && mealPlanCategories.length > 0;
  const hasAmenities = amenities && amenities.length > 0;
  const hasHighlights = highlights && highlights.length > 0;

  return (
    <div className="flex flex-col gap-[30px]">
      {/* Section Title */}
      <span className="block font-noe-display font-normal text-[26px] leading-[33px] text-black">{name}</span>

      {/* Hotel Description */}
      {description && (
        <div className="flex flex-col gap-[5px] font-pt-sans">
          <SeeMoreTextCollapsible
            expandText="Show more"
            collapseText="Show less"
            backgroundColor="#FFFFFF"
            ctaClassName="!text-15px"
            wrapperClassName="!bottom-[2px]"
          >
            <span className="block font-pt-sans text-[15px] leading-[22px] text-black">{description}</span>
          </SeeMoreTextCollapsible>
        </div>
      )}

      {/* Meal Plans */}
      {hasMealPlans && (
        <div className="flex flex-col gap-[10px]">
          <span className="block font-pt-sans font-bold text-[17px] leading-[22px] text-black">Meal Plans</span>
          <div className="flex flex-wrap gap-[10px]">
            {mealPlanCategories.map(mealPlan => (
              <span
                key={mealPlan}
                className="inline-flex justify-center items-center bg-gray-10 text-black font-pt-sans text-[15px] leading-[19px] px-[5px] py-[5px] rounded-[4px]"
              >
                {mealPlan}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Amenities */}
      {hasAmenities && (
        <div className="flex flex-col gap-[10px]">
          <span className="block font-pt-sans font-bold text-[17px] leading-[22px] text-black">Amenities</span>
          <ColumnBulletPoints
            wrapperClassName="font-pt-sans text-[15px] leading-[22px] text-black"
            ulClassName="m-0"
            items={amenities}
            direction="horizontal"
            cols={isMdOrLarger ? 3 : 2}
          />
        </div>
      )}

      {/* Highlights */}
      {hasHighlights && (
        <div className="flex flex-col gap-[10px]">
          <span className="block font-pt-sans font-bold text-[17px] leading-[22px] text-black">Highlights</span>
          <ColumnBulletPoints
            wrapperClassName="font-pt-sans text-[15px] leading-[22px] text-black"
            ulClassName="m-0"
            items={highlights}
            direction="vertical"
            cols={1}
          />
        </div>
      )}

      {/* In Love With */}
      {inLoveWith && (
        <div className="flex flex-col gap-[10px]">
          <span className="block font-pt-sans font-bold text-[17px] leading-[22px] text-black">In Love With</span>
          <span className="block font-pt-sans text-[15px] leading-[22px] text-black">{inLoveWith}</span>
        </div>
      )}

      {/* Things to Be Aware */}
      {additionalInfo && (
        <div className="flex flex-col gap-[10px]">
          <span className="block font-pt-sans font-bold text-[17px] leading-[22px] text-black">Things to Be Aware</span>
          <span className="block font-pt-sans text-[15px] leading-[22px] text-black">{additionalInfo}</span>
        </div>
      )}
    </div>
  );
};
